import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { Course, PendingApply } from '../../lib/types'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { AlertCircle } from 'lucide-react'

const PENDING_APPLY_KEY = 'pending_apply'
const PENDING_APPLY_VERSION = 1
const PENDING_APPLY_EXPIRES_HOURS = 24

export function ApplyPage() {
  useDocumentTitle('講座申込')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [_course, setCourse] = useState<Course | null>(null)

  const coursePublicKey = searchParams.get('course')
  const runPublicKey = searchParams.get('run')
  const source = searchParams.get('source') || 'external'

  useEffect(() => {
    if (!coursePublicKey) {
      setError('講座情報が指定されていません')
      setLoading(false)
      return
    }

    handleApply()
  }, [coursePublicKey, runPublicKey, user])

  const handleApply = async () => {
    if (!coursePublicKey) return

    try {
      setLoading(true)
      setError(null)

      // course_public_keyから講座を取得
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('course_public_key', coursePublicKey)
        .eq('status', 'published')
        .single()

      if (courseError || !courseData) {
        setError('指定された講座が見つかりませんでした')
        setLoading(false)
        return
      }

      setCourse(courseData)

      // ログイン済みの場合、申込確認ページへ
      if (user) {
        const params = new URLSearchParams({
          course: coursePublicKey,
        })
        if (runPublicKey) {
          params.set('run', runPublicKey)
        }
        navigate(`/enrollment/confirm?${params.toString()}`)
        return
      }

      // 未ログインの場合、pending_applyを保存してログインページへ
      const now = new Date()
      const expiresAt = new Date(now.getTime() + PENDING_APPLY_EXPIRES_HOURS * 60 * 60 * 1000)

      const pendingApply: PendingApply = {
        v: PENDING_APPLY_VERSION,
        course: coursePublicKey,
        run: runPublicKey || undefined,
        source,
        created_at: now.toISOString(),
        expires_at: expiresAt.toISOString(),
        return_to: window.location.pathname + window.location.search,
      }

      sessionStorage.setItem(PENDING_APPLY_KEY, JSON.stringify(pendingApply))

      // ログインページへリダイレクト
      navigate('/login', {
        state: {
          message: `「${courseData.title}」への申込にはログインが必要です`,
          from: 'apply',
        },
      })
    } catch (err: any) {
      console.error('Error handling apply:', err)
      setError(err.message || '申込処理に失敗しました')
      setLoading(false)
    }
  }

  if (loading) {
    return <Loading fullScreen message="講座情報を確認中..." />
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-600" />
            <h2 className="mt-6 text-3xl font-bold text-gray-900">エラー</h2>
          </div>

          <ErrorMessage message={error} />

          <div className="text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              ホームへ戻る
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 通常はリダイレクトされるため、この画面は表示されない
  return <Loading fullScreen message="リダイレクト中..." />
}
